/**
 * Checkout Store — Stato globale del flusso checkout.
 *
 * Flusso a due step:
 *   1. drawerOpen    → order-preview-drawer    (scelta pagamento + form fatturazione)
 *   2. summaryOpen   → order-summary-preview-drawer (riepilogo readonly + T&C + conferma)
 *
 * Su desktop affianca il cart-drawer; su mobile lo sostituisce.
 * Se l'utente non è loggato, apre l'auth drawer come secondo pannello
 * e riprende il checkout dopo login/registrazione riusciti.
 *
 * Uso nei template:
 *   $store.checkout.drawerOpen       — true quando il form drawer è aperto
 *   $store.checkout.summaryOpen      — true quando il summary drawer è aperto
 *   $store.checkout.shifting         — true quando il cart è spostato a sinistra
 *   $store.checkout.checkoutPending  — true se l'utente deve autenticarsi prima
 *   $store.checkout.termsAccepted    — true se l'utente ha accettato i T&C
 *   $store.checkout.open()           — avvia il flusso checkout
 *   $store.checkout.openPreview()    — apre il form drawer direttamente
 *   $store.checkout.openSummary()    — valida e passa al riepilogo
 *   $store.checkout.closeSummary()   — torna al form drawer
 *   $store.checkout.close()          — chiude tutto
 *   $store.checkout.confirm()        — crea ordine (chiamato dal summary drawer)
 */
export function registerCheckoutStore(Alpine) {
    Alpine.store('checkout', {
        drawerOpen: false,       // controlla la visibilità del form drawer (step 1)
        summaryOpen: false,      // controlla la visibilità del summary drawer (step 2)
        orderOpen: false,        // controlla la visibilità del order-summary-drawer (step 3)
        termsOpen: false,        // controlla la visibilità del drawer termini e condizioni
        shifting: false,         // controlla lo shift del cart
        animating: false,        // controlla le animazioni CSS interne al cart
        checkoutPending: false,  // true se l'utente deve autenticarsi prima
        termsAccepted: false,    // true se l'utente ha accettato i T&C
        isDesktop: window.innerWidth >= 1024,

        // Dati checkout
        order: null,             // dati dell'ordine creato (step 3)
        orderPaid: false,        // true SOLO quando il pagamento è confermato dal server (mai dal query param)
        // Esito del pagamento al ritorno dal circuito (es. Satispay). Guida la UI del drawer:
        //   'idle'      → nessun ritorno da pagamento in corso (ordine aperto normalmente)
        //   'verifying' → tornati dal circuito, stiamo interrogando il server (polling)
        //   'paid'      → confermato (anche orderPaid=true)
        //   'cancelled' → l'utente ha annullato (Satispay CANCELED) — esito definitivo
        //   'expired'   → sessione scaduta (Satispay EXPIRED) — esito definitivo
        //   'pending'   → timeout senza conferma: stato NEUTRO, mai un falso "fallito"
        paymentOutcome: 'idle',
        paymentLoading: false,   // true durante initPayment()
        paymentError: '',        // messaggio errore nel drawer pagamento
        paymentData: null,       // risposta da /ajax/pay.php (circuit + dati specifici)
        orderPaymentsOpen: false,     // lista metodi espansa nel drawer ordine (cambio metodo)
        orderPaymentsLoading: false,  // true durante il caricamento di /v1/payments per il cambio metodo
        orderPaymentSavingId: null,   // id del metodo in fase di salvataggio via PATCH ordine
        payments: [],
        selectedPaymentId: null,
        occupationName: '',
        useWallet: false,
        walletActive: false,
        walletBalance: 0,
        billing: {
            type: 1,
            subtype: 'private',
            name: '', surname: '', company: '',
            email: '',
            address: '', address_number: '', zip: '', city: '', district: '', nation: 'IT',
            fiscal_code: '', company_fiscal_code: '', vat_number: '',
            sdi_code: '', pec: '',
            phone: '', mobile_phone: '',
        },
        // Snapshot dei campi presenti nel profilo al momento del caricamento.
        // Usato per decidere la visibilità dei campi (non reagisce all'input dell'utente).
        billingProfile: { address: false, zip: false, phone: false, mobile_phone: false },
        profileHasResidence: false, // true se il profilo aveva già un indirizzo al caricamento
        billingGLocation: null, // address_components raw da Google Places (per aggiornamento profilo)
        billingErrors: {},
        loadingData: false,
        submitting: false,
        submitError: '',
        _submitErrorTimer: null,
        _paymentErrorTimer: null,
        _paymentPollTimer: null,

        init() {
            const mq = window.matchMedia('(min-width: 1024px)');
            mq.addEventListener('change', (e) => {
                this.isDesktop = e.matches;
            });
        },

        open() {
            const isLogged = !!window.IKOS?.authToken;

            this.shifting = true;                                // shift cart immediato
            setTimeout(() => { this.animating = true; }, 150);  // animazioni CSS dopo 150ms

            if (isLogged) {
                // Utente loggato: apre direttamente il preview
                this.checkoutPending = false;
                setTimeout(() => { this.drawerOpen = true; this.loadCheckoutData(); }, 500);
            } else {
                // Utente non loggato: apre l'auth drawer come secondo pannello
                this.checkoutPending = true;
                setTimeout(() => { Alpine.store('auth').openDrawerInline(); }, 500);
            }
        },

        openPreview() {
            this.checkoutPending = false;
            this.drawerOpen = true;
            this.loadCheckoutData();
        },

        close() {
            this.drawerOpen = false;
            this.summaryOpen = false;
            this.orderOpen = false;
            this.termsOpen = false;
            this.animating = false;
            this.shifting = false;
            this.checkoutPending = false;
            this.termsAccepted = false;
            this.order = null;
            sessionStorage.removeItem('ik_checkout_pending');
            // Ripristina scroll body se anche cart è chiuso
            if (!Alpine.store('cart').drawerOpen) {
                document.body.style.overflow = '';
            }
        },

        /**
         * Valida il form e apre il summary drawer (step 2).
         * Il form drawer (step 1) viene chiuso con animazione di uscita.
         */
        openSummary() {
            if (!this._validate()) return;
            this._saveBillingToStorage();
            this.drawerOpen = false;
            this.submitError = '';
            this.termsAccepted = false;
            setTimeout(() => { this.summaryOpen = true; }, 280);
        },

        /**
         * Torna al form drawer (step 1) dal summary drawer.
         */
        closeSummary() {
            this.summaryOpen = false;
            setTimeout(() => { this.drawerOpen = true; }, 280);
        },

        /**
         * Apre il drawer dei termini e condizioni d'uso.
         */
        openTerms() {
            this.termsOpen = true;
        },

        /**
         * Chiude il drawer dei termini e condizioni d'uso.
         */
        closeTerms() {
            this.termsOpen = false;
        },

        /**
         * Apre il drawer di riepilogo ordine creato (step 3).
         * Chiude cart e tutti i drawer checkout prima di aprirlo.
         */
        openOrder() {
            this.summaryOpen = false;
            this.drawerOpen = false;
            Alpine.store('cart').closeDrawer();
            this.shifting = false;
            this.animating = false;
            setTimeout(() => { this.orderOpen = true; }, 280);
        },

        /**
         * Riprende il pagamento di un ordine esistente (dalla lista ordini profilo).
         * Fetcha i dati dell'ordine dall'API e apre il drawer di pagamento.
         */
        async resumeOrder(orderId) {
            if (!orderId) return;
            this.order = null;
            this.orderPaid = false;
            this.paymentOutcome = 'idle';
            this.paymentData = null;
            this.paymentError = '';
            this.paymentLoading = false;
            this._resetOrderPaymentChange();
            this.orderOpen = true;
            document.body.style.overflow = 'hidden';

            try {
                const res = await this._apiFetch(
                    `${window.IKOS.apiBase}/v1/users/me/orders/${orderId}`
                );
                if (!res.id) {
                    this._showPaymentError('Impossibile caricare l\'ordine.');
                    return;
                }
                this.order = res;
            } catch (e) {
                this._showPaymentError('Errore di connessione. Riprova.');
            }
        },

        /**
         * Ritorno dal circuito di pagamento (es. Satispay) atterrando sulla lista ordini.
         * Riusa resumeOrder per recuperare l'ordine e aprire il drawer sopra una pagina
         * piena (non la pagina ordine spoglia), poi avvia la verifica server-side dell'esito.
         */
        async verifyReturnedPayment(orderId) {
            if (!orderId) return;
            await this.resumeOrder(orderId);   // fetch ordine + apre il drawer (stato 'idle')
            if (!this.order) return;           // fetch fallito: resumeOrder ha già mostrato l'errore
            this.paymentOutcome = 'verifying';
            this.pollPaymentStatus(String(orderId));
        },

        /**
         * Chiude il drawer ordine e ripristina stato.
         */
        closeOrder() {
            this.orderOpen     = false;
            this.order         = null;
            this.orderPaid     = false;
            this.paymentOutcome = 'idle';
            this.paymentData   = null;
            this.paymentError  = '';
            this.paymentLoading = false;
            this._resetOrderPaymentChange();
            if (this._paymentPollTimer) { clearTimeout(this._paymentPollTimer); this._paymentPollTimer = null; }
            document.body.style.overflow = '';
        },

        _resetOrderPaymentChange() {
            this.orderPaymentsOpen     = false;
            this.orderPaymentsLoading  = false;
            this.orderPaymentSavingId  = null;
        },

        /**
         * Titolo dell'hero nel drawer ordine, coerente con l'esito reale del pagamento.
         * Mai "Ordine confermato!" finché il pagamento non è davvero confermato dal server.
         */
        orderHeroTitle() {
            if (this.orderPaid) return 'Ordine confermato!';
            switch (this.paymentOutcome) {
                case 'verifying': return 'Verifica del pagamento…';
                case 'cancelled': return 'Pagamento annullato';
                case 'expired':   return 'Sessione di pagamento scaduta';
                case 'pending':   return 'Pagamento in verifica';
                default:          return this.order?.payment_required ? 'Ordine creato' : 'Ordine confermato!';
            }
        },

        /**
         * True se il cliente può ancora cambiare il metodo di pagamento dell'ordine:
         * ordine in attesa di pagamento, non pagato, nessuna operazione in corso.
         */
        canChangeOrderPayment() {
            return !!this.order?.payment_required
                && !this.orderPaid
                && !this.paymentLoading
                && !this.orderPaymentsLoading
                && !this.orderPaymentSavingId;
        },

        /**
         * Metodi mostrati nel selettore del drawer ordine.
         * Finché /v1/payments non è caricato c'è solo il metodo corrente; se il metodo
         * corrente non è più tra quelli attivi viene comunque mostrato in testa.
         */
        orderPaymentOptions() {
            const current = this.order?.payment;
            if (!current) return [];
            const list = this.payments.length ? this.payments : [current];
            return list.some(p => p.id === current.id) ? list : [current, ...list];
        },

        /**
         * Carica i metodi di pagamento (GET /v1/payments) se non già in memoria.
         */
        async ensurePayments() {
            if (this.payments.length) return;
            const res = await this._apiFetch(`${window.IKOS.apiBase}/v1/payments`);
            this.payments = (res._embedded?.items ?? []).filter(p => !p.disabled);
        },

        /**
         * Click su una card metodo nel drawer ordine (stesso comportamento del checkout inline):
         *   - metodo corrente → apre/chiude la lista degli altri metodi
         *   - altro metodo    → PATCH /v1/users/me/orders/{id} { payments_id } e richiude la lista
         *
         * L'API restituisce l'ordine aggiornato (metodo + eventuali variazioni sul totale),
         * che sostituisce integralmente `order`.
         */
        async selectOrderPayment(paymentId) {
            if (!this.canChangeOrderPayment()) return;

            if (paymentId === this.order?.payment?.id) {
                if (this.orderPaymentsOpen) {
                    this.orderPaymentsOpen = false;
                    return;
                }
                this.orderPaymentsLoading = true;
                try {
                    await this.ensurePayments();
                    this.orderPaymentsOpen = true;
                } catch (e) {
                    this._showPaymentError('Errore di connessione. Riprova.');
                } finally {
                    this.orderPaymentsLoading = false;
                }
                return;
            }

            this.orderPaymentSavingId = paymentId;
            try {
                const res = await this._apiFetch(
                    `${window.IKOS.apiBase}/v1/users/me/orders/${this.order.id}`,
                    { method: 'PATCH', body: JSON.stringify({ payments_id: paymentId }) },
                );
                if (!res.id) {
                    this._showPaymentError(res.detail || 'Impossibile cambiare il metodo di pagamento.');
                    return;
                }
                this.order = res;
                // Istruzioni bonifico / bottoni PayPal del metodo precedente non valgono più
                this.paymentData  = null;
                this.paymentError = '';
                this.orderPaymentsOpen = false;
            } catch (e) {
                this._showPaymentError('Errore di connessione. Riprova.');
            } finally {
                this.orderPaymentSavingId = null;
            }
        },

        /**
         * Verifica dell'esito al ritorno dal pagamento (es. Satispay, ?payment=return).
         *
         * Satispay non usa più il callback push (rotto dal cutover) e riporta sempre allo
         * STESSO url sia a conferma sia ad annullamento: l'esito NON si deduce dall'URL.
         * Qui interroghiamo /ajax/payment-status.php, che fa sì che l'API interroghi Satispay
         * e chiami _orderPaid solo se ACCEPTED, e ci restituisce lo stato grezzo del provider.
         *
         * Macchina a stati (this.paymentOutcome):
         *   res.paid               → 'paid'  (+ orderPaid=true) — unico via al verde di successo
         *   provider CANCELED/EXPIRED → 'cancelled'/'expired' — esito definitivo, stop subito
         *   altrimenti (PENDING/ignoto) → continua; al timeout 'pending' (neutro, mai "fallito")
         *
         * Con flow MATCH_CODE il pagamento può essere ancora PENDING nell'istante del ritorno:
         * polliamo con backoff fisso (ogni 2s, max ~20s). La finalizzazione server è idempotente.
         */
        pollPaymentStatus(orderId, attempt = 0) {
            const MAX_ATTEMPTS = 10;
            const INTERVAL_MS  = 2000;
            if (!orderId) return;

            // Primo giro: segnala che stiamo verificando (la UI mostra "verifica in corso",
            // non il successo). orderPaid resta false finché il server non conferma davvero.
            if (attempt === 0 && this.paymentOutcome !== 'paid') {
                this.paymentOutcome = 'verifying';
            }

            const retry = () => {
                if (attempt + 1 < MAX_ATTEMPTS) {
                    this._paymentPollTimer = setTimeout(
                        () => this.pollPaymentStatus(orderId, attempt + 1),
                        INTERVAL_MS,
                    );
                } else {
                    // Timeout senza esito definitivo: stato NEUTRO. Non dichiariamo mai
                    // "fallito" un pagamento che potrebbe ancora confermarsi (PENDING lento).
                    if (this.paymentOutcome === 'verifying') this.paymentOutcome = 'pending';
                    this._paymentPollTimer = null;
                }
            };

            fetch(`${window.IKOS.ajaxBase}/payment-status.php?order_id=${encodeURIComponent(orderId)}`, {
                headers: { 'Accept': 'application/json' },
            })
                .then(r => r.json().catch(() => ({})))
                .then(res => {
                    // Conferma reale dal server: unico modo per il verde "pagamento ricevuto".
                    if (res && res.paid) {
                        this.orderPaid = true;
                        this.paymentOutcome = 'paid';
                        this._paymentPollTimer = null;
                        return;
                    }
                    // Esito NEGATIVO e DEFINITIVO: l'utente ha annullato o la sessione è scaduta.
                    // Satispay non cambierà più stato → fermiamo subito il polling.
                    const provider = (res && res.provider_status) ? res.provider_status : '';
                    if (provider === 'CANCELED' || provider === 'EXPIRED') {
                        this.paymentOutcome = provider === 'EXPIRED' ? 'expired' : 'cancelled';
                        this.paymentData = null;   // riespone il CTA "Paga ora" per il retry
                        this._paymentPollTimer = null;
                        return;
                    }
                    // PENDING o stato non ancora noto: continua a verificare.
                    retry();
                })
                .catch(() => {
                    retry();
                });
        },

        /**
         * Avvia il pagamento per l'ordine corrente.
         *
         * Chiama POST /ajax/pay.php → riceve circuit + dati specifici.
         * Branch per circuit:
         *   - paypal-standard: carica SDK PayPal e monta il bottone nel div #ik-paypal-buttons
         *   - satispay:        redirect diretto a redirect_url (il ritorno su ?payment=return avvia la verifica)
         *   - bonifico:        salva dati in paymentData, il template mostra coordinate bancarie
         */
        async initPayment() {
            if (!this.order?.id) return;
            if (this.paymentLoading) return;

            this.paymentLoading = true;
            this.paymentError   = '';
            this.paymentData    = null;

            try {
                const res = await fetch(`${window.IKOS.ajaxBase}/pay.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ order_id: this.order.id }),
                }).then(r => r.json().catch(() => ({})));

                if (!res.success) {
                    this._showPaymentError(res.message || 'Errore durante l\'avvio del pagamento.');
                    return;
                }

                this.paymentData = res.data;

                if (res.circuit === 'satispay') {
                    // Redirect diretto — il ritorno avviene su /users/me/orders/{id}?drawer=true&payment=return
                    // (parametro neutro: l'esito reale lo stabilisce pollPaymentStatus, non l'URL)
                    window.location.href = res.data.redirect_url;
                    return;
                }

                if (res.circuit === 'paypal-standard') {
                    await this._initPaypal(res.data);
                    return;
                }

                // bonifico: il template reagisce a paymentData.circuit === 'bonifico'

            } catch (e) {
                this._showPaymentError('Errore di connessione. Riprova.');
            } finally {
                this.paymentLoading = false;
            }
        },

        /**
         * Carica il PayPal JS SDK e monta il bottone nel div #ik-paypal-buttons.
         * Dopo l'approvazione chiama /ajax/capture.php.
         */
        async _initPaypal(data) {
            const clientId      = data.client_id;
            const paypalOrderId = data.paypal_order_id;
            const isSandbox     = !!data.sandbox;
            const orderId       = this.order.id;

            // Carica SDK solo se non già presente.
            // In sandbox l'SDK viene da sandbox.paypal.com, in produzione da paypal.com.
            if (!window.paypal) {
                const sdkHost = isSandbox ? 'www.sandbox.paypal.com' : 'www.paypal.com';
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = `https://${sdkHost}/sdk/js?client-id=${clientId}&currency=EUR&components=buttons`;
                    script.onload  = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            }

            const container = document.getElementById('ik-paypal-buttons');
            if (!container) {
                this._showPaymentError('Errore inizializzazione PayPal. Riprova.');
                return;
            }

            // Svuota eventuali bottoni precedenti
            container.innerHTML = '';

            // Riferimento temporale per la telemetria: il tempo trascorso qui distingue
            // "popup chiusa subito" da "ha insistito a lungo", tipico di carte rifiutate.
            const mountedAt = Date.now();

            window.paypal.Buttons({
                // Usa l'ordine PayPal già creato server-side
                createOrder: () => paypalOrderId,

                onApprove: async () => {
                    this.paymentLoading = true;
                    this.paymentError   = '';
                    try {
                        const capture = await fetch(`${window.IKOS.ajaxBase}/capture.php`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ order_id: orderId, paypal_order_id: paypalOrderId }),
                        }).then(r => r.json().catch(() => ({})));

                        if (capture.paid) {
                            this.orderPaid = true;
                        } else {
                            this._showPaymentError(capture.message || 'Pagamento non completato. Riprova.');
                        }
                    } catch (e) {
                        this._showPaymentError('Errore durante la cattura del pagamento. Riprova.');
                    } finally {
                        this.paymentLoading = false;
                    }
                },

                // L'utente ha chiuso la popup senza approvare. È l'unico segnale che ci
                // resta quando la carta viene rifiutata dentro il form PayPal: quel
                // rifiuto non passa dal nostro server e non arriva nemmeno a onError.
                onCancel: () => {
                    this._logPaypalEvent(orderId, paypalOrderId, 'cancel', mountedAt);
                    this.paymentLoading = false;
                },

                onError: (err) => {
                    console.error('PayPal error:', err);
                    this._logPaypalEvent(orderId, paypalOrderId, 'error', mountedAt, err);
                    this._showPaymentError('Errore PayPal. Riprova o scegli un altro metodo.');
                    this.paymentLoading = false;
                },

                style: {
                    layout: 'vertical',
                    color:  'gold',
                    shape:  'rect',
                    label:  'pay',
                    height: 48,
                },
            }).render('#ik-paypal-buttons');
        },

        /**
         * Invia al server un evento PayPal osservabile solo lato browser.
         *
         * Serve a datare gli abbandoni: se nel log PayPal un ordine mostra "order created"
         * seguito da un "client cancel" dopo un minuto e mezzo, e nessuna capture, il
         * sospetto è un rifiuto carta avvenuto dentro la popup di paypal.com.
         *
         * Fire-and-forget: un errore di rete qui non deve mai emergere all'utente, che
         * sta già gestendo un pagamento non riuscito.
         */
        _logPaypalEvent(orderId, paypalOrderId, event, mountedAt, err = null) {
            try {
                fetch(`${window.IKOS.ajaxBase}/paypal-event.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    keepalive: true,
                    body: JSON.stringify({
                        order_id:        orderId,
                        paypal_order_id: paypalOrderId,
                        event,
                        elapsed_ms:      Date.now() - mountedAt,
                        detail:          err ? String(err.message || err).slice(0, 500) : '',
                    }),
                }).catch(() => {});
            } catch (e) { /* la telemetria non deve mai rompere il checkout */ }
        },

        /**
         * True se il saldo salvadanaio copre l'intero totale del carrello.
         */
        walletCoversTotal() {
            return this.walletBalance >= Alpine.store('cart').total;
        },

        /**
         * True se il totale del carrello è 0 (ordine gratuito, nessun pagamento richiesto).
         */
        isFree() {
            return Alpine.store('cart').total <= 0;
        },

        /**
         * Carica metodi di pagamento, saldo salvadanaio e profilo utente.
         * Pre-compila il billing con priorità: backend > localStorage > vuoto.
         * I campi con valore da backend sono marcati in billingProfile (readonly nel template).
         */
        async loadCheckoutData() {
            if (this.loadingData) return;
            this.loadingData = true;
            this.submitError = '';
            this.useWallet = false;
            try {
                // Carica metodi di pagamento, saldo salvadanaio e profilo in parallelo
                const [paymentsRes, walletRes, profileRes] = await Promise.all([
                    this._apiFetch(`${window.IKOS.apiBase}/v1/payments`),
                    this._apiFetch(`${window.IKOS.apiBase}/v1/users/me/wallet`),
                    this._apiFetch(`${window.IKOS.apiBase}/v1/users/me`),
                ]);

                this.walletActive = walletRes.active || false;
                this.walletBalance = walletRes.balance || 0;

                // Filtra pagamenti non disabilitati
                this.payments = (paymentsRes._embedded?.items ?? []).filter(p => !p.disabled);

                // Se c'è un solo metodo, pre-selezionalo
                if (this.payments.length === 1) {
                    this.selectedPaymentId = this.payments[0].id;
                }

                // Professione (prima disciplina del profilo)
                this.occupationName = profileRes.disciplines?.[0]?.occupation_name || '';

                // Mappa campi profilo API → campi billing
                const profile = {
                    name:           profileRes.name           || '',
                    surname:        profileRes.surname        || '',
                    email:          profileRes.email          || '',
                    fiscal_code:    profileRes.fiscal_code    || '',
                    address:        profileRes.address        || '',
                    address_number: profileRes.house_number   || '',
                    zip:            profileRes.zip_code       || '',
                    city:           profileRes.city           || '',
                    district:       profileRes.district       || '',
                    region:         profileRes.region         || '',
                    nation:         profileRes.nation         || '',
                    phone:          profileRes.phone          || '',
                    mobile_phone:   profileRes.mobile_phone   || '',
                };

                // Recupera dati salvati in localStorage (fallback per campi non nel profilo)
                const saved = this._loadBillingFromStorage();

                // Costruisce billing: backend ha priorità, poi localStorage, poi vuoto.
                // billingProfile traccia quali campi vengono da backend (readonly nel template).
                const bp = {};
                const billing = {
                    type: 1,
                    subtype: saved.subtype || 'private',
                };

                // Campi che possono venire dal profilo backend
                const profileFields = [
                    'name', 'surname', 'email', 'fiscal_code',
                    'address', 'address_number', 'zip', 'city', 'district', 'region',
                    'phone', 'mobile_phone',
                ];
                // Indirizzo e città restano sempre editabili (precompilati dal profilo):
                // la fattura può andare a una sede diversa, es. società/ente.
                const alwaysEditable = ['address', 'address_number', 'zip', 'city', 'district', 'region'];
                profileFields.forEach(field => {
                    const fromBackend = profile[field]?.trim();
                    if (fromBackend) {
                        billing[field] = fromBackend;
                        bp[field] = !alwaysEditable.includes(field);
                    } else {
                        billing[field] = saved[field] || '';
                        bp[field] = false;
                    }
                });

                // nation: profilo > localStorage > 'IT' (default)
                billing.nation = profile.nation?.trim() || saved.nation || 'IT';

                // Campi solo fatturazione (mai dal profilo, solo localStorage)
                billing.company             = saved.company             || '';
                billing.vat_number          = saved.vat_number          || '';
                billing.sdi_code            = saved.sdi_code            || '';
                billing.pec                 = saved.pec                 || '';
                // CF per fattura società: precompilato con CF del profilo, ma campo indipendente
                billing.company_fiscal_code = saved.company_fiscal_code || profile.fiscal_code || '';

                this.billing = billing;
                this.billingProfile = bp;
                this.profileHasResidence = !!profile.address.trim();
                this.billingErrors = {};
            } catch (e) {
                console.error('checkout.loadCheckoutData:', e);
                this._showError('Errore nel caricamento dei dati. Riprova.');
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Validazione client-side prima di inviare.
         * Restituisce true se tutto ok, false se ci sono errori (popola billingErrors/submitError).
         */
        _validate() {
            const b = this.billing;
            const errors = {};

            // Metodo di pagamento (non richiesto se totale = 0 o se il salvadanaio copre tutto)
            const needsPayment = !this.isFree() && (!this.useWallet || !this.walletCoversTotal());
            if (needsPayment && this.payments.length > 0 && !this.selectedPaymentId) {
                this._showError('Seleziona un metodo di pagamento.');
                return false;
            }

            // Dati fatturazione — condizionali per subtype.
            // Saltati per ordini gratuiti (niente fattura), ma i dati di contatto
            // sotto restano comunque obbligatori se mancanti dal profilo (servono
            // per gli attestati ECM e i recapiti, anche per gli ordini a 0/convenzione).
            if (!this.isFree()) {
                if (b.subtype === 'company') {
                    if (!b.company?.trim()) errors.company = 'Campo obbligatorio.';
                    if (!b.vat_number?.trim()) errors.vat_number = 'Campo obbligatorio.';
                } else if (b.subtype === 'freelance') {
                    if (!b.vat_number?.trim()) errors.vat_number = 'Campo obbligatorio.';
                }
                // SDI o PEC obbligatori (almeno uno) per chiunque abbia P.IVA: la fattura
                // elettronica li richiede per liberi professionisti e società/enti.
                if (b.subtype !== 'private' && !b.sdi_code?.trim() && !b.pec?.trim()) {
                    errors.sdi_code = 'Inserisci il Codice SDI o la PEC.';
                    errors.pec = 'Inserisci il Codice SDI o la PEC.';
                }
            }

            // Dati di contatto — solo se il campo non era nel profilo
            if (!this.billingProfile.address) {
                if (!b.address?.trim()) errors.address = 'Campo obbligatorio.';
                if (!b.address_number?.trim()) errors.address_number = 'Campo obbligatorio.';
            }
            if (!this.billingProfile.zip) {
                if (!b.zip?.trim()) errors.zip = 'Campo obbligatorio.';
                if (!b.city?.trim()) errors.city = 'Campo obbligatorio.';
                // Provincia valorizzata solo dalla scelta in elenco: se la città è stata
                // riscritta a mano l'errore va mostrato sul campo città (district non ha input).
                if (!b.district?.trim()) {
                    errors.district = 'Campo obbligatorio.';
                    if (!errors.city) errors.city = 'Seleziona la città dall\'elenco.';
                }
            }
            // Telefono o cellulare obbligatori (almeno uno), solo se non già nel profilo
            if (!this.billingProfile.phone && !this.billingProfile.mobile_phone) {
                if (!b.phone?.trim() && !b.mobile_phone?.trim()) {
                    errors.phone = 'Inserisci almeno un numero di telefono.';
                    errors.mobile_phone = 'Inserisci almeno un numero di telefono.';
                }
            }

            this.billingErrors = errors;
            if (Object.keys(errors).length > 0) {
                this._showError('Compila i campi obbligatori.');
                return false;
            }
            return true;
        },

        /**
         * Salva pagamento + billing nel carrello, poi crea l'ordine.
         * Usa il proxy PHP che gestisce sessione e auth server-side.
         */
        async confirm() {
            this.billingErrors = {};
            this.submitError = '';

            // Termini e condizioni
            if (!this.termsAccepted) {
                this._showError('Devi accettare i termini e condizioni d\'uso.');
                return;
            }

            this.submitting = true;

            // Mappa subtype → tipo_cliente legacy: 1=privato, 2=libero professionista, 3=società/ente
            const subtypeMap = { private: 1, freelance: 2, company: 3 };
            this.billing.type = subtypeMap[this.billing.subtype] ?? 1;

            // Normalizza CF in uppercase prima dell'invio
            if (this.billing.fiscal_code) {
                this.billing.fiscal_code = this.billing.fiscal_code.toUpperCase();
            }
            if (this.billing.company_fiscal_code) {
                this.billing.company_fiscal_code = this.billing.company_fiscal_code.toUpperCase();
            }

            try {
                // Per company, il CF sulla fattura è company_fiscal_code (distinto dal fiscal_code del profilo).
                // Costruiamo un billing_information con il CF corretto senza alterare billing.fiscal_code.
                const billingForApi = { ...this.billing };
                if (this.billing.subtype === 'company') {
                    billingForApi.fiscal_code = this.billing.company_fiscal_code || '';
                } else {
                    // Ragione sociale è esclusiva di società/ente
                    billingForApi.company = '';
                }
                if (this.billing.subtype === 'private') {
                    // Privato: niente dati di fattura elettronica (no P.IVA, SDI, PEC).
                    // Freelance e company li conservano: con P.IVA SDI o PEC sono obbligatori.
                    billingForApi.vat_number = '';
                    billingForApi.sdi_code   = '';
                    billingForApi.pec        = '';
                }
                delete billingForApi.company_fiscal_code;

                // Ordine gratuito: niente fattura, ma inviamo comunque i dati di
                // contatto (indirizzo/città/telefono) così l'API li salva sul profilo.
                // Azzeriamo i campi fiscali e forziamo "privato" per non generare
                // riferimenti di fatturazione su un ordine a 0 (es. utenti convenzione).
                if (this.isFree()) {
                    billingForApi.type       = 1;
                    billingForApi.subtype    = 'private';
                    billingForApi.company    = '';
                    billingForApi.vat_number = '';
                    billingForApi.sdi_code   = '';
                    billingForApi.pec        = '';
                }

                const body = { billing_information: billingForApi };
                if (this.selectedPaymentId) {
                    body.payments_id = this.selectedPaymentId;
                }
                if (this.useWallet) {
                    body.use_wallet = true;
                }
                const cartToken = localStorage.getItem('ik_cart_token');
                if (cartToken) body.cart_token = cartToken;

                const res = await fetch(`${window.IKOS.ajaxBase}/checkout.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }).then(r => r.json().catch(() => ({})));

                if (res.success) {
                    this.order = res.order;

                    // Aggiorna il profilo utente con i campi compilati nel checkout (fire & forget)
                    this._updateProfileFromBilling();

                    // Azzera il carrello (svuotato lato server dalla creazione ordine)
                    const cart = Alpine.store('cart');
                    cart.items = [];
                    cart.count = 0;
                    cart.subtotal = 0;
                    cart.discount = 0;
                    cart.tax = 0;
                    cart.stampTax = 0;
                    cart.total = 0;
                    cart.totalCredits = 0;
                    cart.promoText = '';
                    cart.discountLabel = '';
                    cart._fetched = false;
                    document.cookie = 'ik_cart_count=0;path=/;max-age=2592000;SameSite=Lax';
                    // Rimuovi il cart_token guest (il carrello è stato convertito in ordine)
                    localStorage.removeItem('ik_cart_token');
                    this.openOrder();
                    return;
                }

                // Sessione server scaduta (inattività o logout in un'altra scheda): il proxy
                // l'ha già ripulita. Riapre il login come secondo pannello del checkout;
                // dopo l'accesso openPreview() ricarica i dati e il billing salvato da openSummary().
                if (res.session_expired) {
                    window.IKOS.authToken = null;
                    const auth = Alpine.store('auth');
                    auth.isLogged = false;
                    this.summaryOpen = false;
                    this.termsAccepted = false;
                    this.checkoutPending = true;
                    setTimeout(() => {
                        auth.openDrawerInline();
                        auth.error = res.message;
                    }, 280);
                    return;
                }

                // Errori di validazione
                if (res.validation_messages) {
                    this.billingErrors = res.validation_messages.billing_information || {};
                    this._showError(res.validation_messages.payments_id
                        ? 'Seleziona un metodo di pagamento.'
                        : (res.message || 'Verifica i dati e riprova.'));
                } else {
                    this._showError(res.message || 'Errore imprevisto. Riprova.');
                }
            } catch (e) {
                this._showError('Errore di connessione. Riprova.');
            } finally {
                this.submitting = false;
            }
        },

        // ── Helpers privati ───────────────────────────────────────────

        /**
         * Setta submitError con auto-dismiss dopo 5 secondi.
         */
        _showError(msg) {
            clearTimeout(this._submitErrorTimer);
            this.submitError = msg;
            this._submitErrorTimer = setTimeout(() => { this.submitError = ''; }, 5000);
        },

        /**
         * Setta paymentError con auto-dismiss dopo 5 secondi.
         */
        _showPaymentError(msg) {
            clearTimeout(this._paymentErrorTimer);
            this.paymentError = msg;
            this._paymentErrorTimer = setTimeout(() => { this.paymentError = ''; }, 5000);
        },

        _apiFetch(url, options = {}) {
            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(options.headers || {}),
            };
            if (window.IKOS?.authToken) {
                headers['Auth-Token'] = window.IKOS.authToken;
            }
            return fetch(url, { ...options, headers }).then(r => r.json().catch(() => ({})));
        },

        _cartPatch(body) {
            const apiBase = window.IKOS?.apiBase || '';
            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
            const token = localStorage.getItem('ik_cart_token');
            if (token) headers['X-Cart-Token'] = token;
            if (window.IKOS?.authToken) headers['Auth-Token'] = window.IKOS.authToken;
            return fetch(`${apiBase}/v1/cart`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(body),
            }).then(r => r.json().catch(() => ({})));
        },

        /**
         * Aggiorna il profilo utente con i dati compilati nel billing che non erano
         * già presenti nel profilo backend (fire & forget, errori silenziosi).
         */
        _updateProfileFromBilling() {
            const patch = {};
            const fieldMap = {
                address:        'address',
                address_number: 'house_number',
                phone:          'phone',
                mobile_phone:   'mobile_phone',
                fiscal_code:    'fiscal_code',
            };
            // Società/ente: l'indirizzo è la sede dell'azienda, non la residenza del corsista.
            // Se il profilo ne ha già una non va sovrascritta (stesso criterio dell'API checkout).
            const keepResidence = this.billing.subtype === 'company' && !this.isFree() && this.profileHasResidence;
            if (keepResidence) {
                delete fieldMap.address;
                delete fieldMap.address_number;
            }
            for (const [billingKey, apiKey] of Object.entries(fieldMap)) {
                if (!this.billingProfile[billingKey] && this.billing[billingKey]?.trim()) {
                    patch[apiKey] = this.billing[billingKey].trim();
                }
            }
            // city/zip/district/region/nation: preferisce g_location (Google Places, include tutto),
            // altrimenti salva i campi singoli. Inviati sempre se disponibili (non solo se mancanti
            // nel profilo) perché il secondo ordine potrebbe avere dati più completi del primo.
            if (keepResidence) {
                // residenza invariata
            } else if (this.billingGLocation) {
                patch.g_location = this.billingGLocation;
            } else {
                if (this.billing.zip?.trim())      patch.zip_code  = this.billing.zip.trim();
                if (this.billing.city?.trim())     patch.city      = this.billing.city.trim();
                if (this.billing.district?.trim()) patch.district  = this.billing.district.trim();
                if (this.billing.region?.trim())   patch.region    = this.billing.region.trim();
                if (this.billing.nation?.trim())   patch.nation    = this.billing.nation.trim();
            }
            if (Object.keys(patch).length === 0) return;
            this._apiFetch(`${window.IKOS.apiBase}/v1/users/me`, {
                method: 'PATCH',
                body: JSON.stringify(patch),
            }).catch(() => {});
        },

        /**
         * Salva i dati di fatturazione inseriti dall'utente su localStorage.
         * Salva solo i campi NON provenienti dal profilo backend.
         */
        _saveBillingToStorage() {
            const data = { subtype: this.billing.subtype };
            const editableFields = [
                'address', 'address_number', 'zip', 'city', 'district', 'region', 'nation',
                'phone', 'mobile_phone',
                'company', 'vat_number', 'sdi_code', 'pec', 'fiscal_code',
            ];
            editableFields.forEach(f => {
                if (!this.billingProfile[f]) {
                    data[f] = this.billing[f] || '';
                }
            });
            try {
                localStorage.setItem('ik_billing', JSON.stringify(data));
            } catch (e) { /* quota exceeded, ignore */ }
        },

        /**
         * Carica i dati di fatturazione salvati da localStorage.
         * @returns {object} dati salvati o oggetto vuoto
         */
        _loadBillingFromStorage() {
            try {
                return JSON.parse(localStorage.getItem('ik_billing')) || {};
            } catch (e) {
                return {};
            }
        },
    });
}
